import { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
} from "@mui/material";
import { FaPlus, FaTrashAlt } from "react-icons/fa";
import { MdEdit } from "react-icons/md";
import StatusBadge from "../../generals/StatusBadge";
import LogicContext from "../../Context/LogicContext";
import { designationService } from "../../services/designation.service";
import DesignationSideScroll from "./DesignationSideScroll";
import DesignationOverview from "./DesignationOverview";
import DesignationFormModal from "./DesignationFormModal";

function isActive(designation) {
  return Number(designation?.statusId) === 1;
}

function designationToastMessage(message) {
  return /designation/i.test(message) ? message : `Designation: ${message}`;
}

function DesignationDetailsPage() {
  const [designation, setDesignation] = useState(null);
  const [designations, setDesignations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState("");
  const [formMode, setFormMode] = useState("");
  const [confirmAction, setConfirmAction] = useState(null);
  const [error, setError] = useState("");
  const latestRequest = useRef(0);
  const logicContext = useContext(LogicContext);
  const navigate = useNavigate();
  const { id } = useParams();
  const { pathname } = useLocation();
  const url = pathname.split("/");
  const currModuleId = url[1];
  const currModuleName = url[2];
  const currUserMenu = url[3] || "designations";
  const basePath = `/${currModuleId}/${currModuleName}/${currUserMenu}`;

  useEffect(() => {
    document.title = "Designation Details";
  }, []);

  useEffect(() => {
    if (!id) return undefined;

    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;
    setLoading(true);
    setError("");

    Promise.all([
      designationService.getDesignation(id),
      designationService.getDesignations(),
    ])
      .then(([detail, list]) => {
        if (latestRequest.current !== requestId) return;
        setDesignation(detail);
        setDesignations(list);
      })
      .catch((loadError) => {
        if (latestRequest.current !== requestId) return;
        setError(
          loadError?.message
            ? designationToastMessage(loadError.message)
            : "Unable to load designation details."
        );
        setDesignation(null);
      })
      .finally(() => {
        if (latestRequest.current === requestId) setLoading(false);
      });

    return () => {
      latestRequest.current += 1;
    };
  }, [id]);

  function viewDesignationDetails(designationId) {
    navigate(`${basePath}/${designationId}`);
  }

  async function refreshAfterWrite() {
    const [detail, list] = await Promise.all([
      designationService.getDesignation(id),
      designationService.getDesignations(),
    ]);
    setDesignation(detail);
    setDesignations(list);
  }

  async function refreshListAfterWrite() {
    setDesignations(await designationService.getDesignations());
  }

  async function handleEditDesignation({ name, statusId, departmentId }) {
    setActionLoading("edit");
    try {
      const result = await designationService.updateDesignation(
        id,
        name,
        statusId,
        departmentId
      );
      logicContext.showToast(
        designationToastMessage(result.message),
        "success"
      );
      try {
        await refreshAfterWrite();
      } catch (refreshError) {
        logicContext.showToast(
          designationToastMessage(
            refreshError?.message || "Unable to refresh designation details."
          ),
          "error"
        );
      }
      setFormMode("");
    } catch (editError) {
      logicContext.showToast(
        editError?.message || "Unable to update designation.",
        "error"
      );
      throw editError;
    } finally {
      setActionLoading("");
    }
  }

  async function handleCreateDesignation({ name, departmentId }) {
    setActionLoading("create");
    try {
      const result = await designationService.createDesignation(
        name,
        departmentId
      );
      logicContext.showToast(
        designationToastMessage(result.message),
        "success"
      );
      try {
        await refreshListAfterWrite();
      } catch (refreshError) {
        logicContext.showToast(
          designationToastMessage(
            refreshError?.message || "Unable to refresh designations."
          ),
          "error"
        );
      }
      setFormMode("");
    } catch (createError) {
      logicContext.showToast(
        createError?.message || "Unable to create designation.",
        "error"
      );
      throw createError;
    } finally {
      setActionLoading("");
    }
  }

  function handleStatusChange() {
    const action = isActive(designation) ? "deactivate" : "activate";
    setConfirmAction({
      action: "status",
      title: `Confirm ${action} Designation`,
      message: `Are you sure you want to ${action} the designation "${designation.name}"?`,
    });
  }

  async function applyStatusChange() {
    const nextStatusId = isActive(designation) ? 2 : 1;
    setActionLoading("status");
    try {
      const result = await designationService.setDesignationStatus(
        id,
        nextStatusId
      );
      logicContext.showToast(
        designationToastMessage(result.message),
        "success"
      );
      try {
        await refreshAfterWrite();
      } catch (refreshError) {
        logicContext.showToast(
          designationToastMessage(
            refreshError?.message || "Unable to refresh designation details."
          ),
          "error"
        );
      }
    } catch (statusError) {
      logicContext.showToast(
        designationToastMessage(
          statusError?.message || "Unable to update designation status."
        ),
        "error"
      );
    } finally {
      setActionLoading("");
    }
  }

  function handleDeleteDesignation() {
    setConfirmAction({
      action: "delete",
      title: "Confirm Delete Designation",
      message: `The designation "${designation.name}" will be permanently removed. Do you want to continue?`,
    });
  }

  async function applyDeleteDesignation() {
    setActionLoading("delete");
    try {
      const result = await designationService.deleteDesignation(id);
      logicContext.showToast(
        designationToastMessage(result.message),
        "success"
      );
      try {
        await refreshListAfterWrite();
      } catch (refreshError) {
        logicContext.showToast(
          designationToastMessage(
            refreshError?.message || "Unable to refresh designations."
          ),
          "error"
        );
      }
      navigate(basePath);
    } catch (deleteError) {
      logicContext.showToast(
        designationToastMessage(
          deleteError?.message || "Unable to delete designation."
        ),
        "error"
      );
    } finally {
      setActionLoading("");
    }
  }

  function confirmPendingAction() {
    const action = confirmAction?.action;
    setConfirmAction(null);
    if (action === "status") return applyStatusChange();
    if (action === "delete") return applyDeleteDesignation();
  }

  const actionsDisabled = loading || Boolean(actionLoading) || !designation;

  return (
    <div className="@container">
      <div className="mt-2 flex flex-col @xs:flex-row @xs:items-center @xs:justify-end">
        <div className="mr-6 @xs:mt-2 w-fit flex flex-row items-center px-2.5 py-[2px] border-[1px] border-gray-200 bg-white rounded-full">
          <StatusBadge
            statusId={isActive(designation) ? 1 : "I"}
            statusName={designation?.statusName || "-"}
          />
        </div>

        <div className="mt-2 flex flex-row items-center">
          <Tooltip title="Delete Designation">
            <button
              onClick={handleDeleteDesignation}
              disabled={actionsDisabled}
              className="mr-2 px-3 py-2.5 bg-gray-200 border-[1px] border-gray-300 rounded-md text-white focus:outline-none font-medium hover:bg-gray-100 active:bg-gray-200 transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FaTrashAlt className="h-[16px] w-[16px] text-gray-600" />
            </button>
          </Tooltip>
          <Tooltip title="Edit Designation">
            <button
              onClick={() => setFormMode("edit")}
              disabled={actionsDisabled}
              className="mr-2 px-3 py-2.5 bg-gray-200 border-[1px] border-gray-300 rounded-md text-white focus:outline-none font-medium hover:bg-gray-100 active:bg-gray-200 transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <MdEdit className="h-[16px] w-[16px] text-gray-600" />
            </button>
          </Tooltip>
          <Tooltip title="Add Designation">
            <button
              onClick={() => setFormMode("create")}
              disabled={actionsDisabled}
              className="px-3 py-2.5 bg-gray-200 border-[1px] border-gray-300 rounded-md text-white focus:outline-none font-medium hover:bg-gray-100 active:bg-gray-200 transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FaPlus className="h-[16px] w-[16px] text-gray-600" />
            </button>
          </Tooltip>
        </div>
      </div>

      <div className="mt-2 flex justify-end">
        <button
          onClick={handleStatusChange}
          disabled={actionsDisabled}
          className="px-4 py-2 text-sm rounded-md bg-gray-200 text-gray-700 font-medium hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isActive(designation)
            ? "Deactivate"
            : "Activate"}
        </button>
      </div>

      <div className="mt-6 w-full flex flex-col @xl:flex-row">
        <div className="my-1 mx-1 w-full @xl:w-[210px] flex-shrink-0 bg-white">
          <DesignationSideScroll
            designations={designations}
            viewDesignationDetails={viewDesignationDetails}
          />
        </div>

        <div className="mt-10 @xl:mt-0 w-full @xl:ml-4">
          <div className="pb-2 border-b-[1.5px] border-b-gray-200 @xl:px-4 flex flex-row flex-wrap @md:justify-between max-w-3xl">
            <div className="mt-2 mr-2 text-sm font-medium cursor-pointer text-primary">
              Designation Overview
            </div>
          </div>

          <div className="mt-2 max-auto max-w-6xl text-filterTextColor">
            {loading ? (
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="h-8 w-1/2 rounded bg-gray-100" />
                <div className="mt-4 h-5 rounded bg-gray-100" />
                <div className="mt-3 h-5 rounded bg-gray-100" />
                <div className="mt-3 h-5 rounded bg-gray-100" />
              </div>
            ) : null}

            {!loading && error ? (
              <div className="rounded-lg border border-red-100 bg-red-50 p-5 text-sm text-red-700">
                {error}
              </div>
            ) : null}

            {!loading && !error && !designation ? (
              <div className="rounded-lg border border-gray-200 bg-white p-5 text-sm text-gray-600">
                Designation not found.
              </div>
            ) : null}

            {!loading && !error && designation ? (
              <DesignationOverview designation={designation} />
            ) : null}
          </div>
        </div>
      </div>

      {formMode ? (
        <DesignationFormModal
          mode={formMode}
          initialValues={
            formMode === "edit"
              ? {
                  name: designation?.name,
                  statusId: designation?.statusId,
                  departmentId: designation?.departmentId,
                  departmentName: designation?.departmentName,
                }
              : { name: "", statusId: 1 }
          }
          onSubmit={
            formMode === "edit"
              ? handleEditDesignation
              : handleCreateDesignation
          }
          onClose={() => setFormMode("")}
        />
      ) : null}

      <Dialog
        open={Boolean(confirmAction)}
        onClose={() => setConfirmAction(null)}
        aria-labelledby="designation-confirm-title"
      >
        <DialogTitle id="designation-confirm-title">
          {confirmAction?.title}
        </DialogTitle>
        <DialogContent>{confirmAction?.message}</DialogContent>
        <DialogActions className="px-6 pb-4">
          <Button onClick={() => setConfirmAction(null)}>
            Cancel Designation Action
          </Button>
          <Button
            variant="contained"
            color={confirmAction?.action === "delete" ? "error" : "primary"}
            onClick={confirmPendingAction}
          >
            Confirm Designation Action
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default DesignationDetailsPage;
